import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import Chiclet from 'components/Chiclet';
import RecapCardItem from 'components/RelatedSearches/RecapCardItem.f';
import ChunkletItem from 'components/RelatedSearches/ChunkletItem.f';
import { relatedSearchesVariantSelector } from 'viewModel/selectors/testTarget/relatedSearchesVariantSelector';
import { relatedSearchTermsSelector } from 'selectors/page/relatedSearch/relatedSearchTermsSelector';
import { MAX_RELATED_SEARCH_TERMS, MIN_RELATED_SEARCH_TERMS, isRelatedSearchesEligible } from 'components/RelatedSearches/constants';
import validateMultiTermSearchResponse from 'components/RelatedSearches/schemas/MultiTermSearchResponseSchema';
import useMultiTermSearch from 'hooks/useMultiTermSearch';
import localeUtils from 'utils/LanguageLocale';
import Debounce from 'utils/Debounce';
import Location from 'utils/Location';
import { space, colors } from 'style/config';
import UI from 'utils/UI';
import { RELATED_SEARCHES_EXPERIENCE } from 'constants/TestTarget';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const SEARCH_PATH = '/search';
const DEBOUNCE_MS = 300; // AC-023
const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * Related Searches section (SBR-3530 shell + SBR-3531 chiclet + SBR-3529 recap card + SBR-3532 chunklet).
 * Self-contained: resolves its own variant + terms via selectors, applies the SAME eligibility
 * predicate the grid reserves on (so a hidden state never leaves an empty reserved cell),
 * caps to 8 (BR-003), and branches on variant to render chiclet rail, recap card grid, or chunklet rail.
 * Returns null in Control / pending / ineligible states (AC-028 render-nothing).
 * Phase 1 (P1-001): Variant branching for CHALLENGER_TWO + CHALLENGER_THREE
 * Phase 4 (P4-004): Manual validation with silent failure (governance-compliant, no Zod)
 * Phase 5 (P5-002): BR-014 cascade hide if term count < 3
 */
// eslint-disable-next-line complexity
function RelatedSearches({ productRowsCount }) {
    const variant = useSelector(relatedSearchesVariantSelector);
    const terms = useSelector(relatedSearchTermsSelector, shallowEqual);
    const [tappedIndex, setTappedIndex] = useState(null);

    // Phase 1 (P1-001): Fetch images for CHALLENGER_TWO variant via multiTermSearch
    // Gate on eligibility to avoid unnecessary requests when ineligible
    const cappedTerms = terms?.slice(0, MAX_RELATED_SEARCH_TERMS) || [];
    const isEligible = isRelatedSearchesEligible(variant, terms, productRowsCount);
    const shouldFetchImages =
        (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO || variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE) && isEligible;

    // SBR-3532: CHALLENGER_THREE uses 1 image per term (chunklet), CHALLENGER_TWO uses 4 (2×2 grid)
    const resultCount = variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE ? 1 : 4;

    const {
        data: multiTermSearchData,
        loading: multiTermSearchLoading,
        error: multiTermSearchError
    } = useMultiTermSearch({
        keywords: shouldFetchImages ? cappedTerms : [],
        resultCount
    });

    // Debounced tap-to-search (AC-023): preventDoubleClick fires once, then suppresses repeats within
    // the window. Navigates the same way as the search bar (SiteSearch) / category chiclets.
    const handleTermClick = useMemo(
        () =>
            Debounce.preventDoubleClick(term => {
                // SBR-3535 ANALYTICS HOOK: fire related_search_click here (out of scope this bundle).
                Location.navigateTo(null, `${SEARCH_PATH}?keyword=${encodeURIComponent(term)}`);
            }, DEBOUNCE_MS),
        []
    );

    // SAME predicate as the grid reservation gate: Control / pending / < 3 terms / < 2 rows => render nothing.
    if (!isEligible) {
        return null;
    }

    const title = getText('relatedSearchesTitle');

    // Phase 4 (P4-004): Manual validation for CHALLENGER_TWO variant (governance-compliant, no Zod)
    let validatedData = null;

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO && multiTermSearchData) {
        const validationResult = validateMultiTermSearchResponse(multiTermSearchData);

        if (!validationResult.success) {
            if (typeof Sephora !== 'undefined') {
                Sephora?.logger?.warn('[RelatedSearches] multiTermSearch validation failed:', validationResult.error);
            }

            return null; // P4-004: section hides on validation error
        }

        validatedData = { results: validationResult.data };
    }

    // Phase 5 (P5-002): BR-014 cascade - hide section if < 3 TERMS (not < 3 cards with images)
    // BR-014: "Cards are NEVER hidden for missing images" - RecapCardItem handles 0 images with placeholders
    let filteredResults = [];

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO && validatedData) {
        filteredResults = validatedData.results; // Show ALL cards (AC-013, AC-014)

        // P5-002: Cascade hide if total TERMS < 3 (not filtered by image count)
        if (validatedData.results.length < MIN_RELATED_SEARCH_TERMS) {
            // SBR-3535 ANALYTICS HOOK: fire load_failed event here (out of scope this bundle)
            return null; // Section hidden when term count < 3
        }
    }

    // SBR-3532: CHALLENGER_THREE image map (BR-014 compliant — never hide for missing images)
    // On error or validation failure, chunkletImageMap stays null => all chunklets render with placeholders
    let chunkletImageMap = null;

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE && multiTermSearchData && !multiTermSearchError) {
        const validationResult = validateMultiTermSearchResponse(multiTermSearchData);

        if (validationResult.success) {
            // Build a term -> images lookup for chunklet rendering
            chunkletImageMap = {};
            validationResult.data.forEach(result => {
                chunkletImageMap[result.searchTerm] = result.thumbnailImages || [];
            });
        } else if (typeof Sephora !== 'undefined') {
            Sephora?.logger?.warn('[RelatedSearches] multiTermSearch validation failed:', validationResult.error);
        }
    }

    // ---- VARIANT BRANCHING (Phase 1 P1-001) ----

    // CHALLENGER_TWO: Recap card grid with 2×2 images
    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO) {
        // Phase 1 (P1-002): Section-level skeleton during fetch
        if (multiTermSearchLoading) {
            return (
                <Box
                    aria-busy='true'
                    css={[
                        UI.SKELETON_ANIMATION,
                        {
                            height: '200px',
                            backgroundColor: colors.gray[100]
                        }
                    ]}
                />
            );
        }

        // Error or no data => silent failure (already handled by validation above)
        if (multiTermSearchError || !filteredResults.length) {
            return null;
        }

        return (
            <Box>
                <Text
                    is='h2'
                    fontSize={['base', 'md']}
                    fontWeight='bold'
                    lineHeight='tight'
                    marginBottom={2}
                    children={title}
                />
                <ul
                    aria-label={title}
                    css={styles.recapScrollRow}
                >
                    {filteredResults.map((result, i) => (
                        <RecapCardItem
                            key={result.searchTerm}
                            term={result.searchTerm}
                            images={result.thumbnailImages}
                            onClick={() => {
                                setTappedIndex(i);
                                handleTermClick(result.searchTerm);
                            }}
                            isActive={tappedIndex === i}
                        />
                    ))}
                </ul>
            </Box>
        );
    }

    // SBR-3532: CHALLENGER_THREE: Chunklet variant (term label + single product image)
    // BR-014: Chunklets are NEVER hidden for missing images — render with placeholders on error/validation failure
    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE) {
        // Section-level skeleton during multiTermSearch fetch (P1-006, AC-022)
        if (multiTermSearchLoading) {
            return (
                <Box
                    role='status'
                    aria-label={getText('loadingRelatedSearches')}
                    aria-busy='true'
                    css={[
                        UI.SKELETON_ANIMATION,
                        {
                            height: `${space[20]}px`, // ~120px (space[20] = 120 in design tokens)
                            backgroundColor: colors.gray[100]
                        }
                    ]}
                />
            );
        }

        return (
            <Box>
                <Text
                    is='h2'
                    fontSize={['base', 'md']}
                    fontWeight='bold'
                    lineHeight='tight'
                    marginBottom={2}
                    children={title}
                />
                <ul
                    aria-label={title}
                    css={styles.chunkletScrollRow}
                >
                    {cappedTerms.map((term, i) => {
                        const images = chunkletImageMap?.[term] || [];

                        return (
                            <ChunkletItem
                                key={term}
                                term={term}
                                images={images}
                                onClick={() => {
                                    setTappedIndex(i);
                                    handleTermClick(term);
                                }}
                            />
                        );
                    })}
                </ul>
            </Box>
        );
    }

    // CHALLENGER_ONE: Chiclet rail (existing implementation)
    return (
        <Box>
            <Text
                is='h2'
                fontSize={['base', 'md']}
                fontWeight='bold'
                lineHeight='tight'
                marginBottom={2}
                children={title}
            />
            <ul
                aria-label={title}
                css={styles.scrollRow}
            >
                {cappedTerms.map((term, i) => (
                    <li key={term}>
                        <Chiclet
                            is='button'
                            type='button'
                            variant='shadow'
                            isActive={tappedIndex === i}
                            aria-pressed={tappedIndex === i}
                            aria-label={getText('searchForTerm', [term])}
                            onClick={() => {
                                setTappedIndex(i);
                                handleTermClick(term);
                            }}
                            children={term}
                        />
                    </li>
                ))}
            </ul>
        </Box>
    );
}

// Co-located styles per UFE convention
const styles = {
    // Horizontal scroll-row recipe for chiclet variant (CHALLENGER_ONE)
    scrollRow: {
        margin: `-${space[2]}px -${space.container}px`,
        padding: `${space[2]}px ${space.container}px`,
        fontSize: 0,
        whiteSpace: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        li: {
            display: 'inline-block',
            '+ li': {
                marginLeft: space[2]
            }
        }
    },
    // Horizontal scroll layout for chunklet variant (CHALLENGER_THREE, SBR-3532)
    chunkletScrollRow: {
        display: 'flex',
        gap: `${space[3]}px`,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        listStyle: 'none',
        padding: 0,
        margin: 0,
        li: {
            flexShrink: 0,
            width: `${space[16]}px` // 80px (space[16] = 80 in design tokens)
        }
    },
    // Horizontal scroll layout for recap card variant (CHALLENGER_TWO, AC-015)
    recapScrollRow: {
        display: 'flex',
        gap: `${space[2]}px`,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        listStyle: 'none',
        padding: 0,
        margin: 0,
        li: {
            flexShrink: 0,
            width: '146px' // Figma spec: fixed card width
        }
    }
};

RelatedSearches.propTypes = {
    productRowsCount: PropTypes.number
};

export default wrapFunctionalComponent(RelatedSearches, 'RelatedSearches');
